// One warehouse rule for regular products and bundle parent variants.
(function () {
  const componentRequests = new Map();
  const componentQuery = `query BundleWarehouse($id: ID!) {
    node(id: $id) {
      ... on ProductVariant {
        id
        components(first: 250) {
          nodes { quantity productVariant { id selectedOptions { name value } } }
          pageInfo { hasNextPage }
        }
      }
    }
  }`;

  function componentWarehouse(components) {
    if (!components?.nodes?.length || components.pageInfo?.hasNextPage !== false) return '';
    const warehouses = components.nodes.map(function (component) {
      if (!(component.quantity > 0)) return '';
      const values = (component.productVariant?.selectedOptions || [])
        .filter(option => String(option.name).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().includes('almacen'))
        .map(option => String(option.value || '').trim());
      return values.length && values[0] && values.every(value => value === values[0]) ? values[0] : '';
    });
    return warehouses[0] && warehouses.every(code => code === warehouses[0]) ? warehouses[0] : '';
  }

  function loadComponentWarehouse(id) {
    const gid = String(id).startsWith('gid://') ? String(id) : 'gid://shopify/ProductVariant/' + id;
    if (!componentRequests.has(gid)) {
      const request = (async function () {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 10000);
        try {
          // Tokenless Storefront read on the CURRENT store, including QA/password cookies.
          const response = await fetch('/api/2026-10/graphql.json', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
            body: JSON.stringify({ query: componentQuery, variables: { id: gid } })
          });
          if (!response.ok) throw new Error('Shopify no pudo devolver los componentes del combo.');
          const result = await response.json();
          if (result.errors?.length || result.data?.node?.id !== gid) {
            throw new Error('No fue posible validar los componentes del combo.');
          }
          return componentWarehouse(result.data.node.components);
        } finally {
          clearTimeout(timeout);
        }
      })();
      componentRequests.set(gid, request);
      request.catch(() => componentRequests.delete(gid));
    }
    return componentRequests.get(gid);
  }

  async function resolveVariants(variants) {
    if (!Array.isArray(variants)) return [];
    return Promise.all(variants.map(async function (variant) {
      if (!variant.is_bundle) return variant;
      try {
        const code = await loadComponentWarehouse(variant.id);
        return { ...variant, warehouse_code: code, warehouse_resolution: code ? 'components' : 'unassigned' };
      } catch (_) {
        // No stale/manual mapping or first-available fallback if validation fails.
        return { ...variant, warehouse_code: '', warehouse_resolution: 'error' };
      }
    }));
  }

  function warehouseCode(variant, optionNames) {
    if (Object.prototype.hasOwnProperty.call(variant, 'warehouse_code')) {
      return String(variant.warehouse_code || '').trim();
    }
    const values = [];
    (optionNames || []).forEach(function (option, index) {
      const name = String(option?.name || option).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      if (name.includes('almacen')) {
        values.push(String(variant.options?.[index] ?? variant['option' + (index + 1)] ?? '').trim());
      }
    });
    // A bundle may expose a warehouse option per component. Never mix stores.
    if (values.length) return values[0] && values.every(value => value === values[0]) ? values[0] : '';
    return String(variant.title || '').trim();
  }

  function selectVariant(variants, code, optionNames) {
    const selectedCode = String(code || '').trim();
    if (!selectedCode || !Array.isArray(variants)) return null;
    const matches = variants.filter(variant => warehouseCode(variant, optionNames) === selectedCode);
    // Do not guess if more than one variant needs additional customer choices.
    return matches.length === 1 ? matches[0] : null;
  }

  function searchVariants(response) {
    const product = response?.product || response;
    if (!Array.isArray(product?.variants)) return [];
    const tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',');
    const isBundle = tags.some(tag => ['combo', 'bundle', 'shopify-bundle'].includes(String(tag).trim().toLowerCase()));
    return product.variants.filter(variant => variant && typeof variant === 'object').map(variant => ({
      ...variant,
      warehouse_code: warehouseCode(variant, product.options),
      is_bundle: isBundle,
      // Shopify .js uses cents; legacy .json responses use decimal strings.
      price: typeof variant.price === 'string' ? Math.round(Number(variant.price) * 100) : variant.price,
      compare_at_price: typeof variant.compare_at_price === 'string'
        ? Math.round(Number(variant.compare_at_price) * 100) : variant.compare_at_price
    }));
  }

  window.SuKarneWarehouse = Object.freeze({ warehouseCode, selectVariant, componentWarehouse, resolveVariants, searchVariants });
})();
